import alert from '@jetbrains/ring-ui/components/alert-service/alert-service'
import Button from '@jetbrains/ring-ui/components/button/button'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import * as React from 'react'
import type {SyntheticEvent} from 'react'
import {useMutation} from 'react-relay'
import type {PayloadError} from 'relay-runtime'

import type {AgentPoolPageBulkAssignProjectWithAgentPoolMutation} from '../../__generated__/AgentPoolPageBulkAssignProjectWithAgentPoolMutation.graphql'
import {bulkAssignProjectWithAgentPool} from '../../AgentPoolPage.queries'
import {walkItemAndDescendants} from '../../AgentPoolPage.selectors'
import type {AgentPoolProjectsTreeProjectItem} from '../../AgentPoolPage.types'

import styles from './UndoDiassociateProjectButton.css'

const writableEmptyArrayOfStrings: Array<string> = []
function onError(error: Error | PayloadError): void | null {
  alert.error(error.message)
}

function UndoDiassociateProjectButton({
  agentPoolRawId,
  unassignedProjectItem,
  onCompleted,
  recursively,
}: {
  agentPoolRawId: string
  onCompleted: (projectRawIds: ReadonlyArray<string>) => void
  unassignedProjectItem: AgentPoolProjectsTreeProjectItem
  recursively: boolean
}): React.ReactElement | null {
  const [commitAssign, isAssignmentInFlight] =
    useMutation<AgentPoolPageBulkAssignProjectWithAgentPoolMutation>(bulkAssignProjectWithAgentPool)

  let projectsToAssignInclusively: Array<string> = writableEmptyArrayOfStrings
  let projectsToAssignExclusively: Array<string> = writableEmptyArrayOfStrings

  const callback = ({rawId, agentPools}: AgentPoolProjectsTreeProjectItem) => {
    // In fact, projects with zero agent pools are assigned with the Default pool,
    // so to revert things back we should assign them with a project exclusively.
    if (agentPools.length === 0) {
      projectsToAssignExclusively = [...projectsToAssignExclusively, rawId]
    } else {
      projectsToAssignInclusively = [...projectsToAssignInclusively, rawId]
    }
  }

  if (recursively) {
    walkItemAndDescendants(unassignedProjectItem, callback)
  } else {
    callback(unassignedProjectItem)
  }

  const onClick = React.useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation()
      if (projectsToAssignExclusively.length > 0) {
        commitAssign({
          variables: {
            agentPoolRawId,
            exclusively: true,
            projectRawIds: projectsToAssignExclusively,
          },
          onCompleted: (response, errors) => {
            errors == null ? onCompleted(projectsToAssignExclusively) : errors.forEach(onError)
          },
          onError,
        })
      }
      if (projectsToAssignInclusively) {
        commitAssign({
          variables: {
            agentPoolRawId,
            exclusively: false,
            projectRawIds: projectsToAssignInclusively,
          },
          onCompleted: (response, errors) => {
            errors == null ? onCompleted(projectsToAssignInclusively) : errors.forEach(onError)
          },
          onError,
        })
      }
    },
    [
      commitAssign,
      agentPoolRawId,
      projectsToAssignExclusively,
      projectsToAssignInclusively,
      onCompleted,
    ],
  )

  return isAssignmentInFlight ? (
    <LoaderInline className={styles.loader} />
  ) : (
    <>
      {recursively ? 'Unassigned from this pool with all subprojects' : 'Unassigned from this pool'}
      <Button className={styles.undoButton} text onClick={onClick} title="Undo unassign">
        {'Undo'}
      </Button>
    </>
  )
}

export default React.memo(UndoDiassociateProjectButton)
